import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DollarSign, Lock, Users, User, Building2, BookOpen,
  Globe, BarChart3, Target, Calculator, Shield, LogOut,
  Activity, LayoutDashboard, LineChart, FileText, MoreHorizontal,
  Sun, Moon,
} from 'lucide-react';
import { profiles } from '@/data/profileData';
import { NavDropdown } from './NavDropdown';
import { SettingsButton } from './SettingsModal';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

const NavLink = ({ to, label, icon: Icon, isActive, testId }) => (
  <Link
    to={to}
    className={`px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
      isActive
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
    }`}
    data-testid={testId}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </Link>
);

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const p = location.pathname;

  const tenantId = React.useMemo(() => {
    const tenants = (user?.tenant_access || []).map(t => t.tenant_id);
    const firstSeg = p.split('/')[1];
    return tenants.includes(firstSeg) ? firstSeg : tenants[0] || 'us-soccer';
  }, [p, user]);

  // ── Active-state detection ──
  const intelligenceRoutes = ['executive-dashboard', 'competitive-intelligence', 'roi-simulator', 'brand-dependency', 'analytics'];
  const isIntelligence = intelligenceRoutes.some(r => p === `/${tenantId}/${r}`);
  const isDashboard = p === `/${tenantId}` && !isIntelligence;
  const isTeamsActive = p === '/national-teams';
  const isAthletes = p === '/athletes' || p.startsWith('/athlete/');
  const isClubs = p === '/teams';
  const isProcurement = p === `/${tenantId}/procurement-kit`;
  const isMethodology = p === '/methodology';
  const isMoreActive = isAthletes || isClubs || isMethodology;

  const userInitial = (user?.name || user?.email || 'U')[0].toUpperCase();

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
        <div className="flex items-center justify-between gap-4">
          {/* ── Left: Logo ── */}
          <Link to="/national-teams" className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0" data-testid="nav-logo-home">
            <div className="w-8 h-8 rounded-lg bg-stable/10 border border-stable/20 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-stable" />
            </div>
            <div className="hidden md:block">
              <h1 className="text-sm font-semibold tracking-tight text-foreground leading-none">YBV</h1>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider leading-none mt-0.5">Brand Intelligence</p>
            </div>
          </Link>

          {/* ── Center: Core nav ── */}
          <nav className="flex items-center gap-1 border-l border-r border-border/40 px-4 mx-2" data-testid="main-nav">
            <NavLink to={`/${tenantId}`} label="Dashboard" icon={LayoutDashboard} isActive={isDashboard} testId="nav-dashboard" />

            <NavDropdown
              label="Teams"
              icon={Globe}
              isActive={isTeamsActive}
              testId="nav-teams-dropdown"
              renderContent={(props) => <TeamsContent {...props} tenantId={tenantId} />}
            />

            <NavDropdown
              label="Intelligence"
              icon={LineChart}
              isActive={isIntelligence}
              testId="nav-intelligence-dropdown"
              renderContent={(props) => <IntelligenceContent {...props} tenantId={tenantId} />}
            />

            <NavDropdown
              label="More"
              icon={MoreHorizontal}
              isActive={isMoreActive}
              testId="nav-more-dropdown"
              renderContent={(props) => <MoreContent {...props} />}
            />
          </nav>

          {/* ── Right: Actions + user ── */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={`/${tenantId}/procurement-kit`}
              className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors whitespace-nowrap ${
                isProcurement
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
              data-testid="nav-procurement"
            >
              <FileText className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Procurement Kit</span>
              <span className="lg:hidden">Kit</span>
            </Link>

            <div className="hidden lg:flex items-center gap-1.5 px-2 py-1 rounded-full border border-border/40 text-[10px] text-muted-foreground/60 font-medium uppercase tracking-wider">
              <Lock className="w-2.5 h-2.5" />
              Confidential
            </div>

            <SettingsButton />

            <NavDropdown
              label=""
              icon={null}
              isActive={false}
              testId="nav-user-menu"
              customTrigger={
                <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center text-[11px] font-bold text-primary cursor-pointer hover:bg-primary/25 transition-colors" data-testid="nav-user-avatar">
                  {userInitial}
                </div>
              }
              renderContent={(props) => <UserContent {...props} user={user} logout={logout} navigate={navigate} />}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

// ── Dropdown content ──

function TeamsContent({ isOpen, close, tenantId }) {
  const t = tenantId || 'us-soccer';
  return (
    <>
      <NavDropdown.Header title="National Programs" />
      <NavDropdown.Link to={`/${t}`} label="USMNT" IconComp={<Globe className="w-4 h-4" />} badge="Men" isOpen={isOpen} onClose={close} testId="nav-team-usmnt" />
      <NavDropdown.Link to={`/${t}`} label="USWNT" IconComp={<Globe className="w-4 h-4" />} badge="Women" isOpen={isOpen} onClose={close} testId="nav-team-uswnt" />
      <NavDropdown.Divider />
      <NavDropdown.Link to="/national-teams" label="All National Teams" IconComp={<Users className="w-4 h-4" />} isOpen={isOpen} onClose={close} testId="nav-all-teams" />
    </>
  );
}

function IntelligenceContent({ isOpen, close, tenantId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const t = tenantId || 'us-soccer';
  return (
    <>
      <NavDropdown.Header title="Strategic Analysis" />
      <NavDropdown.Link to={`/${t}/executive-dashboard`} label="Executive Dashboard" IconComp={<BarChart3 className="w-4 h-4" />} isOpen={isOpen} onClose={close} testId="nav-executive-dashboard" />
      <NavDropdown.Link to={`/${t}/competitive-intelligence`} label="Competitive Analysis" IconComp={<Target className="w-4 h-4" />} isOpen={isOpen} onClose={close} testId="nav-competitive-analysis" />
      <NavDropdown.Divider />
      <NavDropdown.Header title="Simulators & Risk" />
      <NavDropdown.Link to={`/${t}/roi-simulator`} label="ROI Simulator" IconComp={<Calculator className="w-4 h-4" />} badge="2026" isOpen={isOpen} onClose={close} testId="nav-roi-simulator" />
      <NavDropdown.Link to={`/${t}/brand-dependency`} label="Brand Dependency" IconComp={<Shield className="w-4 h-4" />} badge="Risk" isOpen={isOpen} onClose={close} testId="nav-brand-dependency" />
      {isAdmin && (
        <>
          <NavDropdown.Divider />
          <NavDropdown.Header title="Admin" />
          <NavDropdown.Link to={`/${t}/analytics`} label="Product Analytics" IconComp={<Activity className="w-4 h-4" />} isOpen={isOpen} onClose={close} testId="nav-analytics" />
        </>
      )}
    </>
  );
}

function MoreContent({ isOpen, close }) {
  const entries = Object.entries(profiles);
  return (
    <>
      <NavDropdown.Header title="Athletes" />
      <NavDropdown.Link to="/athletes" label="All Athletes" IconComp={<Users className="w-4 h-4" />} isOpen={isOpen} onClose={close} testId="nav-all-athletes" />
      {entries.map(function renderProfileLink(entry) {
        const id = entry[0];
        const profile = entry[1];
        return (
          <NavDropdown.Link
            key={id}
            to={'/athlete/' + id}
            label={profile.name}
            IconComp={<User className="w-4 h-4" />}
            isOpen={isOpen}
            onClose={close}
            testId={'nav-athlete-' + id}
          />
        );
      })}
      <NavDropdown.Divider />
      <NavDropdown.Header title="More Sections" />
      <NavDropdown.Link to="/teams" label="Clubs" IconComp={<Building2 className="w-4 h-4" />} badge="Phase 2" isOpen={isOpen} onClose={close} testId="nav-clubs" />
      <NavDropdown.Link to="/methodology" label="Methodology" IconComp={<BookOpen className="w-4 h-4" />} isOpen={isOpen} onClose={close} testId="nav-methodology" />
    </>
  );
}

function UserContent({ isOpen, close, user, logout, navigate }) {
  const { resolvedTheme, setTheme } = useTheme();
  return (
    <>
      <div className="px-3 py-2.5 border-b border-border/30">
        <p className="text-xs font-medium text-foreground truncate">{user?.name || user?.email}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{user?.name ? user.email : null}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{user?.role === 'ADMIN' ? 'Administrator' : 'Viewer'}</p>
      </div>
      <div className="p-1">
        <button
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          data-testid="quick-theme-toggle"
        >
          {resolvedTheme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          {resolvedTheme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
        </button>
        <button
          onClick={() => { close(); logout(); navigate('/login'); }}
          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign Out
        </button>
      </div>
    </>
  );
}
